import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import hisselerVeri from '../data/bist-hisseler.json';
import { birdenFazlaFiyatGetir } from '../services/isyatirimApi';
import { takipListesiniGetir, takipListesineEkle, takipListesindenCikar, takipListesiniSirala } from '../services/watchlistApi';
import { fiyatFormatla, isaretliFormatla, degisimHesapla } from '../utils/fiyat';

const TUM_HISSELER = hisselerVeri.data;

function TakipListem() {
  const [sorgu, setSorgu] = useState('');
  const [secililer, setSecililer] = useState([]);
  const [listeYukleniyor, setListeYukleniyor] = useState(true);
  const [listeHatasi, setListeHatasi] = useState('');
  const [fiyatlar, setFiyatlar] = useState({});
  const [suruklenenIndex, setSuruklenenIndex] = useState(null);
  const [suruklemeOfsetY, setSuruklemeOfsetY] = useState(0);
  const [siralamaHatasi, setSiralamaHatasi] = useState('');

  // Sürükleme sırasında değişmeyen, ama render'lar arasında da
  // korunması gereken değerler (satır yüksekliği px, sürüklemenin
  // başladığı Y konumu) — bunlar render tetiklemesine gerek olmadığı
  // için state yerine ref'te tutuluyor.
  const satirYuksekligiRef = useRef(0);
  const baslangicYRef = useRef(0);

  useEffect(() => {
    let iptalEdildi = false;

    takipListesiniGetir()
      .then(kodlar => {
        if (iptalEdildi) return;
        setSecililer(kodlar);
        setListeYukleniyor(false);
      })
      .catch(err => {
        if (iptalEdildi) return;
        setListeHatasi(err.message);
        setListeYukleniyor(false);
      });

    return () => { iptalEdildi = true; };
  }, []);

  // Fiyatlar sürekli açık bir bağlantıyla değil, istendiğinde (sayfa
  // açıldığında ve navbar'daki "Yenile" düğmesine basıldığında) tek
  // seferlik istekle çekiliyor.
  async function fiyatlariYenile(kodlar) {
    if (kodlar.length === 0) return;

    setFiyatlar(prev => {
      const sonraki = { ...prev };
      kodlar.forEach(kod => {
        sonraki[kod] = { ...(sonraki[kod] || {}), yukleniyor: true };
      });
      return sonraki;
    });

    const sonuclar = await birdenFazlaFiyatGetir(kodlar);
    setFiyatlar(prev => {
      const sonraki = { ...prev };
      kodlar.forEach(kod => {
        sonraki[kod] = { ...sonuclar[kod], yukleniyor: false };
      });
      return sonraki;
    });
  }

  useEffect(() => {
    fiyatlariYenile(secililer);
  }, [secililer]);

  // Navbar'daki "Yenile" düğmesi bu event'i tetikler.
  useEffect(() => {
    function dinleyici() {
      fiyatlariYenile(secililer);
    }
    window.addEventListener('veri-yenile', dinleyici);
    return () => window.removeEventListener('veri-yenile', dinleyici);
  }, [secililer]);

  async function hisseEkle(kod) {
    if (secililer.includes(kod)) return;
    setListeHatasi('');

    try {
      await takipListesineEkle(kod);
      setSecililer([...secililer, kod]);
      setSorgu('');
    } catch (err) {
      setListeHatasi(err.message);
    }
  }

  async function hisseCikar(kod) {
    setListeHatasi('');

    try {
      await takipListesindenCikar(kod);
      setSecililer(secililer.filter(k => k !== kod));
      setFiyatlar(prev => {
        const sonraki = { ...prev };
        delete sonraki[kod];
        return sonraki;
      });
    } catch (err) {
      setListeHatasi(err.message);
    }
  }

  // Sürükleme, HTML5 native drag-and-drop yerine Pointer Events ile
  // yapılıyor: sürüklenen satır fareyi/dokunuşu 1:1 piksel piksel takip
  // eder (translateY), diğer satırlar da yer açmak için CSS geçişiyle
  // kayar — böylece native DnD'nin donuk "hayalet" görünümü yerine
  // gerçek bir kayma hissi oluşur. setPointerCapture sayesinde fare
  // satırın dışına çıksa bile hareket/bırakma olayları aynı elemanda
  // yakalanmaya devam eder.
  function suruklemeBasla(event, index) {
    event.currentTarget.setPointerCapture(event.pointerId);
    satirYuksekligiRef.current = event.currentTarget.closest('tr').offsetHeight;
    baslangicYRef.current = event.clientY;
    setSuruklenenIndex(index);
    setSuruklemeOfsetY(0);
  }

  function suruklemeHareket(event) {
    if (suruklenenIndex === null) return;
    setSuruklemeOfsetY(event.clientY - baslangicYRef.current);
  }

  function suruklemeHedefIndex() {
    if (suruklenenIndex === null || !satirYuksekligiRef.current) return suruklenenIndex;
    const adim = Math.round(suruklemeOfsetY / satirYuksekligiRef.current);
    return Math.max(0, Math.min(secililer.length - 1, suruklenenIndex + adim));
  }

  async function suruklemeBitti(event) {
    if (suruklenenIndex === null) return;
    event.currentTarget.releasePointerCapture(event.pointerId);

    const kaynakIndex = suruklenenIndex;
    const hedefIndex = suruklemeHedefIndex();

    setSuruklenenIndex(null);
    setSuruklemeOfsetY(0);

    if (hedefIndex === kaynakIndex) return;

    const yeni = [...secililer];
    const [tasinan] = yeni.splice(kaynakIndex, 1);
    yeni.splice(hedefIndex, 0, tasinan);
    setSecililer(yeni);

    setSiralamaHatasi('');
    try {
      await takipListesiniSirala(yeni);
    } catch (err) {
      setSiralamaHatasi(err.message);
    }
  }

  function suruklemeIptal(event) {
    if (suruklenenIndex === null) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setSuruklenenIndex(null);
    setSuruklemeOfsetY(0);
  }

  const aramaTerimi = sorgu.trim().toUpperCase();
  const aramaSonuclari = aramaTerimi
    ? TUM_HISSELER
        .filter(h => !secililer.includes(h.kod) && (h.kod.includes(aramaTerimi) || h.ad.toUpperCase().includes(aramaTerimi)))
        .slice(0, 15)
    : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h1 className="text-lg font-bold text-slate-800 mb-1">BIST Hisse Takip</h1>
        <p className="text-sm text-slate-500 mb-4">Takip etmek istediğiniz hisseyi kod veya isimle arayıp listenize ekleyin.</p>

        <input
          type="text" value={sorgu} onChange={e => setSorgu(e.target.value)}
          placeholder="Hisse kodu veya adı ara (ör. THYAO, EREGLI...)"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-all"
        />

        {aramaSonuclari.length > 0 && (
          <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
            {aramaSonuclari.map(h => (
              <li key={h.kod} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                <span className="text-sm">
                  <span className="font-semibold text-slate-800">{h.kod}</span>
                  <span className="text-slate-500"> — {h.ad}</span>
                </span>
                <button
                  type="button" onClick={() => hisseEkle(h.kod)}
                  className="text-xs font-semibold text-sky-600 hover:text-sky-700 shrink-0 ml-3"
                >Ekle</button>
              </li>
            ))}
          </ul>
        )}

        {aramaTerimi && aramaSonuclari.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">Sonuç bulunamadı.</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-lg font-bold text-slate-800 mb-4">Takip Listem</div>

        {listeHatasi && <p className="text-sm text-red-600 mb-3">{listeHatasi}</p>}
        {siralamaHatasi && <p className="text-sm text-red-600 mb-3">{siralamaHatasi}</p>}

        {listeYukleniyor && <p className="text-sm text-slate-500">Yükleniyor...</p>}

        {!listeYukleniyor && secililer.length === 0 && (
          <p className="text-sm text-slate-500">Henüz takip listenize hisse eklemediniz.</p>
        )}

        {!listeYukleniyor && secililer.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-1 w-6"></th>
                  <th className="py-2 pr-3">Kod</th>
                  <th className="py-2 pr-3">Hisse</th>
                  <th className="py-2 pr-3">Son Fiyat</th>
                  <th className="py-2 pr-3">Değişim</th>
                  <th className="py-2 pr-3">Fark</th>
                  <th className="py-2 pr-3">Güncelleme</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const hedefIndex = suruklemeHedefIndex();

                  return secililer.map((kod, index) => {
                    const hisse = TUM_HISSELER.find(h => h.kod === kod);
                    const fiyat = fiyatlar[kod] || {};
                    const degisim = fiyat.last !== undefined ? degisimHesapla(fiyat.last, fiyat.dayClose) : null;
                    const renkSinifi = degisim && degisim.fark < 0 ? 'text-red-600' : 'text-emerald-600';

                    let satirStili;
                    let satirSinifi = 'border-b border-slate-100';

                    if (suruklenenIndex === index) {
                      satirStili = { transform: `translateY(${suruklemeOfsetY}px)`, position: 'relative', zIndex: 10 };
                      satirSinifi += ' shadow-lg bg-white';
                    } else if (suruklenenIndex !== null) {
                      let kayma = 0;
                      if (suruklenenIndex < hedefIndex && index > suruklenenIndex && index <= hedefIndex) kayma = -satirYuksekligiRef.current;
                      else if (suruklenenIndex > hedefIndex && index >= hedefIndex && index < suruklenenIndex) kayma = satirYuksekligiRef.current;
                      satirStili = { transform: `translateY(${kayma}px)`, transition: 'transform 150ms ease' };
                    }

                    return (
                      <tr key={kod} style={satirStili} className={satirSinifi}>
                        <td
                          className="py-2 pr-1 text-slate-300 cursor-grab active:cursor-grabbing select-none"
                          style={{ touchAction: 'none' }}
                          title="Sürükleyerek sırala"
                          onPointerDown={event => suruklemeBasla(event, index)}
                          onPointerMove={suruklemeHareket}
                          onPointerUp={suruklemeBitti}
                          onPointerCancel={suruklemeIptal}
                        >⠿</td>
                        <td className="py-2 pr-3 font-semibold">
                          <NavLink to={`/hisse/${kod.toLowerCase()}`} className="text-slate-800 hover:text-sky-600">
                            {kod}
                          </NavLink>
                        </td>
                        <td className="py-2 pr-3 text-slate-600">{hisse ? hisse.ad : '—'}</td>
                        <td className="py-2 pr-3">
                          {fiyat.hata ? (
                            <span className="text-red-600 text-xs">{fiyat.hata}</span>
                          ) : fiyat.last !== undefined ? (
                            <span className="font-semibold text-slate-800">{fiyatFormatla(fiyat.last)}</span>
                          ) : (
                            <span className="text-slate-400 text-xs">{fiyat.yukleniyor ? 'Yükleniyor...' : '—'}</span>
                          )}
                        </td>
                        <td className={'py-2 pr-3 font-semibold ' + (degisim ? renkSinifi : 'text-slate-400')}>
                          {degisim ? isaretliFormatla(degisim.yuzde, '%') : '—'}
                        </td>
                        <td className={'py-2 pr-3 font-semibold ' + (degisim ? renkSinifi : 'text-slate-400')}>
                          {degisim ? isaretliFormatla(degisim.fark) : '—'}
                        </td>
                        <td className="py-2 pr-3 text-slate-500 text-xs">
                          {fiyat.guncellemeZamani ? fiyat.guncellemeZamani.toLocaleTimeString('tr-TR') : '—'}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button" onClick={() => hisseCikar(kod)}
                            className="text-xs font-semibold text-red-500 hover:text-red-700"
                          >Kaldır</button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-slate-400 mt-4">
          Fiyatlar sayfa açıldığında isyatirim.com.tr'den çekilir; güncellemek için sağ üstteki Yenile düğmesini kullanın.
        </p>
      </div>
    </div>
  );
}

export default TakipListem
